/**
 * WordPress dependencies
 */
import {
    BaseControl,
    Button,
    ButtonGroup,
    ColorIndicator,
    ColorPicker,
    Flex,
    FlexBlock,
    FlexItem,
    GradientPicker,
    Popover,
    RangeControl,
    SelectControl,
    ToggleControl
} from '@wordpress/components';
import { withInstanceId } from '@wordpress/compose';
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const customGradientOptions = [
    {
        name: 'JShine',
        gradient: 'linear-gradient(135deg,#12c2e9 0%,#c471ed 50%,#f64f59 100%)',
        slug: 'jshine'
    },
    {
        name: 'Moonlit Asteroid',
        gradient: 'linear-gradient(135deg,#0F2027 0%, #203A43 0%, #2c5364 100%)',
        slug: 'moonlit-asteroid'
    },
    {
        name: 'Rastafarie',
        gradient: 'linear-gradient(135deg,#1E9600 0%, #FFF200 0%, #FF0000 100%)',
        slug: 'rastafari'
    },
    {
        name: 'Frozen',
        gradient: 'linear-gradient(135deg,#403B4A 0%, #E7E9BB 100%)',
        slug: 'frozen'
    },
    {
        name: 'Mango Pulp',
        gradient: 'linear-gradient(135deg,#F09819 0%, #EDDE5D 100%)',
        slug: 'mango-pulp'
    },
    {
        name: 'Bloody Mary',
        gradient: 'linear-gradient(135deg,#FF512F 0%, #DD2476 100%)'
    }
];

/**
 * Internal Dependencies
 */
import Preview from '../preview';
import ResetButton from '../reset-btn';
import UploadBtn from '../upload-btn';
import ColorControl from '../color-control';

// constant
import { PREFIX } from '../../constants';

const BackgroundControl = ({ instanceId, label, controlName, objAttrs, noImage = true }) => {
    const [overlayColorPanel, setOverlayColorPanel] = useState(false);
    const [bgPanel, setBgPanel] = useState(false);

    const id = `color-control-${instanceId}`;
    const { attributes, setAttributes } = objAttrs;
    const COLORS = wp.data.select('core/editor').getEditorSettings().colors;
    const gradientOptions = wp.data.select('core/editor').getEditorSettings().gradients;

    const {
        [`${PREFIX}${controlName}bgType`]: bgType,
        [`${PREFIX}${controlName}bgColor`]: bgColor,
        [`${PREFIX}${controlName}bgGradient`]: bgGradient,
        [`${PREFIX}${controlName}bgImage`]: bgImage,
        [`${PREFIX}${controlName}bgImageSize`]: bgImageSize,
        [`${PREFIX}${controlName}bgImagePosition`]: bgImagePosition,
        [`${PREFIX}${controlName}bgImageRepeat`]: bgImageRepeat,
        [`${PREFIX}${controlName}bgImageAttachment`]: bgImageAttachment,
        [`${PREFIX}${controlName}bgImageOverlay`]: bgImageOverlay,
        [`${PREFIX}${controlName}bgImageOverlayColor`]: bgImageOverlayColor,
        [`${PREFIX}${controlName}bgImageOverlayOpacity`]: bgImageOverlayOpacity
    } = attributes;

    return (
        <div className="progressmatify-control-container background-control">
            <Flex>
                <FlexBlock>
                    <BaseControl id={id} label={label} />
                </FlexBlock>
                <FlexItem>
                    <Button
                        className={`progressmatify-editor-icon progressmatify-reset-button ${
                            bgColor || bgGradient ? 'active' : 'disabled'
                        }`}
                        icon="image-rotate"
                        label={__('Reset', 'progressmatify-blocks')}
                        onClick={() =>
                            setAttributes({
                                [`${PREFIX}${controlName}bgType`]: '',
                                [`${PREFIX}${controlName}bgColor`]: '',
                                [`${PREFIX}${controlName}bgGradient`]: ''
                            })
                        }
                    />
                </FlexItem>
                <FlexItem>
                    <div className="bg-type">
                        <ButtonGroup>
                            <Button
                                className={`progressmatify-editor-icon ${bgType === 'classic' ? 'active' : ''}`}
                                onClick={() =>
                                    setAttributes({
                                        [`${PREFIX}${controlName}bgType`]: 'classic'
                                    })
                                }
                            >
                                <svg width="11" height="10" viewBox="0 0 11 10">
                                    <path
                                        fill="#1d2327"
                                        d="M10.6927 1.08247C10.6927 1.08247 10.8502 0.615805 10.5119 0.289139C10.2027 -0.0141947 9.80023 0.149139 9.80023 0.149139C9.4444 0.324139 6.44023 2.17331 5.32606 3.39831C4.8244 3.95831 4.12439 5.60914 4.69023 6.20997C5.2269 6.78164 7.00023 6.11081 7.4844 5.62664C8.68606 4.42497 10.5236 1.44414 10.6927 1.08247ZM0.816895 9.29581C2.19939 8.38581 1.66856 7.30664 2.70106 6.58914C3.24356 6.20997 3.99606 6.22747 4.49773 6.75831C4.86523 7.14914 4.9644 8.25747 4.4044 8.77664C3.48856 9.62247 2.07106 9.68081 0.816895 9.29581Z"
                                    ></path>
                                </svg>
                            </Button>
                            <Button
                                className={`progressmatify-editor-icon ${bgType === 'gradient' ? 'active' : ''}`}
                                onClick={() =>
                                    setAttributes({
                                        [`${PREFIX}${controlName}bgType`]: 'gradient'
                                    })
                                }
                            >
                                <svg width="12" height="12" viewBox="0 0 10 10">
                                    <path
                                        fill="#1d2327"
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M1.11111 1.11111V8.88889H8.88889V1.11111H1.11111ZM0.555556 0C0.248731 0 0 0.248731 0 0.555556V9.44444C0 9.75127 0.248731 10 0.555556 10H9.44444C9.75127 10 10 9.75127 10 9.44444V0.555556C10 0.248731 9.75127 0 9.44444 0H0.555556Z"
                                    ></path>
                                    <path fill="#1d2327" d="M1.66667 1.66667H7.77778L1.66667 7.77778V1.66667Z"></path>
                                </svg>
                            </Button>
                        </ButtonGroup>
                    </div>
                </FlexItem>
            </Flex>
            {bgType === 'classic' && (
                <div className="progressmatify-control-container progressmatify-color-control">
                    <ColorControl
                        label={__('Color', 'progressmatify-blocks')}
                        color={bgColor}
                        onChange={color => {
                            setAttributes({
                                [`${PREFIX}${controlName}bgColor`]: color
                            });
                        }}
                    />
                    {!noImage && (
                        <div className="progressmatify-img-background">
                            {bgImage && bgImage?.url ? (
                                <>
                                    <Preview
                                        url={bgImage?.url}
                                        id={bgImage?.id}
                                        onSelect={media => {
                                            setAttributes({
                                                [`${PREFIX}${controlName}bgImage`]: {
                                                    id: media.id,
                                                    url: media.url
                                                }
                                            });
                                        }}
                                        onDelete={() =>
                                            setAttributes({
                                                [`${PREFIX}${controlName}bgImage`]: {
                                                    id: null,
                                                    url: null
                                                }
                                            })
                                        }
                                    />
                                    <Flex>
                                        <FlexBlock>
                                            <BaseControl id={id} label={__('Properties', 'progressmatify-blocks')} />
                                        </FlexBlock>
                                        <FlexItem>
                                            <button className="progressmatify-btn" onClick={() => setBgPanel(true)}>
                                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                    <path d="M8 0a1 1 0 0 1 1 1v5.268l4.562-2.634a1 1 0 1 1 1 1.732L10 8l4.562 2.634a1 1 0 1 1-1 1.732L9 9.732V15a1 1 0 1 1-2 0V9.732l-4.562 2.634a1 1 0 1 1-1-1.732L6 8 1.438 5.366a1 1 0 0 1 1-1.732L7 6.268V1a1 1 0 0 1 1-1" />
                                                </svg>
                                            </button>
                                        </FlexItem>
                                        {bgPanel && (
                                            <Popover
                                                position="bottom right"
                                                onFocusOutside={() => setBgPanel(false)}
                                                offset={10}
                                                className="progressmatify-typography-popover"
                                            >
                                                <div className="progressmatify-bg-panel progressmatify-typography-panel progressmatify-popover">
                                                    <SelectControl
                                                        label={__('Size', 'progressmatify-blocks')}
                                                        value={bgImageSize}
                                                        options={[
                                                            { label: __('Auto', 'progressmatify-blocks'), value: 'auto' },
                                                            { label: __('Cover', 'progressmatify-blocks'), value: 'cover' },
                                                            { label: __('Contain', 'progressmatify-blocks'), value: 'contain' }
                                                        ]}
                                                        onChange={size => {
                                                            setAttributes({
                                                                [`${PREFIX}${controlName}bgImageSize`]: size
                                                            });
                                                        }}
                                                    />
                                                    <SelectControl
                                                        label={__('Position', 'progressmatify-blocks')}
                                                        value={bgImagePosition}
                                                        options={[
                                                            { label: __('Left Top', 'progressmatify-blocks'), value: 'left top' },
                                                            { label: __('Left Center', 'progressmatify-blocks'), value: 'left center' },
                                                            { label: __('Left Bottom', 'progressmatify-blocks'), value: 'left bottom' },
                                                            { label: __('Right Top', 'progressmatify-blocks'), value: 'right top' },
                                                            { label: __('Right Center', 'progressmatify-blocks'), value: 'right center' },
                                                            { label: __('Right Bottom', 'progressmatify-blocks'), value: 'right Bottom' },
                                                            { label: __('Center Top', 'progressmatify-blocks'), value: 'center top' },
                                                            { label: __('Center Center', 'progressmatify-blocks'), value: 'center center' },
                                                            { label: __('Center Bottom', 'progressmatify-blocks'), value: 'center bottom' }
                                                        ]}
                                                        onChange={position => {
                                                            setAttributes({
                                                                [`${PREFIX}${controlName}bgImagePosition`]: position
                                                            });
                                                        }}
                                                    />
                                                    <SelectControl
                                                        label={__('Repeat', 'progressmatify-blocks')}
                                                        value={bgImageRepeat}
                                                        options={[
                                                            { label: __('Repeat', 'progressmatify-blocks'), value: 'repeat' },
                                                            { label: __('No Repeat', 'progressmatify-blocks'), value: 'no-repeat' },
                                                            { label: __('Repeat X', 'progressmatify-blocks'), value: 'repeat-x' },
                                                            { label: __('Repeat Y', 'progressmatify-blocks'), value: 'repeat-y' }
                                                        ]}
                                                        onChange={repeat => {
                                                            setAttributes({
                                                                [`${PREFIX}${controlName}bgImageRepeat`]: repeat
                                                            });
                                                        }}
                                                    />
                                                    <SelectControl
                                                        label={__('Attachment', 'progressmatify-blocks')}
                                                        value={bgImageAttachment}
                                                        options={[
                                                            { label: __('Fixed', 'progressmatify-blocks'), value: 'fixed' },
                                                            { label: __('Scroll', 'progressmatify-blocks'), value: 'scroll' }
                                                        ]}
                                                        onChange={scroll => {
                                                            setAttributes({
                                                                [`${PREFIX}${controlName}bgImageAttachment`]: scroll
                                                            });
                                                        }}
                                                    />
                                                    <ToggleControl
                                                        label={__('Enable Overlay', 'progressmatify-blocks')}
                                                        checked={bgImageOverlay}
                                                        onChange={() =>
                                                            setAttributes({
                                                                [`${PREFIX}${controlName}bgImageOverlay`]: !bgImageOverlay
                                                            })
                                                        }
                                                    />
                                                    {bgImageOverlay && (
                                                        <>
                                                            <Flex>
                                                                <FlexBlock>
                                                                    <BaseControl
                                                                        id={id}
                                                                        label={__('Overlay Color', 'progressmatify-blocks')}
                                                                    />
                                                                </FlexBlock>
                                                                <FlexItem>
                                                                    <Button
                                                                        icon="image-rotate"
                                                                        label={__('Reset', 'progressmatify-blocks')}
                                                                        onClick={() =>
                                                                            setAttributes({
                                                                                [`${PREFIX}${controlName}bgImageOverlayColor`]: ''
                                                                            })
                                                                        }
                                                                        className={`progressmatify-reset-button ${
                                                                            bgImageOverlayColor ? 'active' : 'disabled'
                                                                        }`}
                                                                    />
                                                                </FlexItem>
                                                                <FlexItem>
                                                                    <button
                                                                        className="color-indicator"
                                                                        onClick={() => setOverlayColorPanel(true)}
                                                                    >
                                                                        <ColorIndicator colorValue={bgImageOverlayColor} />
                                                                    </button>
                                                                    {overlayColorPanel && (
                                                                        <Popover
                                                                            position="bottom right"
                                                                            onFocusOutside={() => setOverlayColorPanel(false)}
                                                                            offset={10}
                                                                        >
                                                                            <div className="progressmatify-color-panel">
                                                                                <ColorPicker
                                                                                    color={bgImageOverlayColor}
                                                                                    onChangeComplete={value =>
                                                                                        setAttributes({
                                                                                            [`${PREFIX}${controlName}bgImageOverlayColor`]:
                                                                                                value.hex
                                                                                        })
                                                                                    }
                                                                                    disableAlpha={false}
                                                                                />
                                                                                <div className="progressmatify-colors-palette">
                                                                                    <label
                                                                                        className="progressmatify-label progressmatify-mb-8"
                                                                                        htmlFor="progressmatify-colors-palette"
                                                                                    >
                                                                                        {__('Colors Palette', 'progressmatify-blocks')}
                                                                                    </label>
                                                                                    <div id="progressmatify-colors-palette">
                                                                                        {COLORS &&
                                                                                            COLORS.map((paletteColor, index) => {
                                                                                                return (
                                                                                                    <ColorIndicator
                                                                                                        className={`progressmatify-color-indicator ${
                                                                                                            paletteColor.color ===
                                                                                                            bgImageOverlayColor
                                                                                                                ? 'active'
                                                                                                                : ''
                                                                                                        }`}
                                                                                                        title={paletteColor.name}
                                                                                                        key={index}
                                                                                                        colorValue={paletteColor.color}
                                                                                                        onClick={() =>
                                                                                                            setAttributes({
                                                                                                                [`${PREFIX}${controlName}bgImageOverlayColor`]:
                                                                                                                    paletteColor.color
                                                                                                            })
                                                                                                        }
                                                                                                    />
                                                                                                );
                                                                                            })}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </Popover>
                                                                    )}
                                                                </FlexItem>
                                                            </Flex>
                                                            <div className="single-rangle">
                                                                <ResetButton
                                                                    onReset={() =>
                                                                        setAttributes({
                                                                            [`${PREFIX}${controlName}bgImageOverlayOpacity`]: 0.5
                                                                        })
                                                                    }
                                                                    value={bgImageOverlayOpacity}
                                                                >
                                                                    <RangeControl
                                                                        value={bgImageOverlayOpacity}
                                                                        onChange={value =>
                                                                            setAttributes({
                                                                                [`${PREFIX}${controlName}bgImageOverlayOpacity`]: value
                                                                            })
                                                                        }
                                                                        min={0}
                                                                        max={1}
                                                                        step={0.1}
                                                                    />
                                                                </ResetButton>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </Popover>
                                        )}
                                    </Flex>
                                </>
                            ) : (
                                <UploadBtn
                                    title={__('Background Image', 'progressmatify-blocks')}
                                    onSelect={media => {
                                        setAttributes({
                                            [`${PREFIX}${controlName}bgImage`]: {
                                                id: media.id,
                                                url: media.url
                                            }
                                        });
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
            {bgType === 'gradient' && (
                <div className="progressmatify-control-container progressmatify-gradient-control">
                    <BaseControl id={id} label={__('Gradient', 'progressmatify-blocks')} />
                    <GradientPicker
                        value={bgGradient || 'linear-gradient(135deg,#12c2e9 0%,#c471ed 50%,#f64f59 100%)'}
                        onChange={v => {
                            setAttributes({
                                [`${PREFIX}${controlName}bgGradient`]: v
                            });
                        }}
                        gradients={gradientOptions || customGradientOptions}
                        clearable={false}
                    />
                </div>
            )}
        </div>
    );
};

export default withInstanceId(BackgroundControl);
